import { PartnershipLevel, PaymentMethod, PropertyPaymentMethod } from '../src/core/constants';
import type Campaign from './Campaign';
import { FAQ } from './Faq';
import HotelRoom from './RoomAvailability';

export type Coupon = {
  id: number | string;
  couponCode: string;
};

export type HotelPhoto = {
  category: string;
  alt: string;
  url: string;
  hidden: boolean;
  selection: string;
  metadata: {
    width: number;
    height: number;
  };
};

export type HotelPhotos = {
  categories: string[];
  selectedPhotos: HotelPhoto[];
  allPhotos: HotelPhoto[];
  categorizedPhotos: Record<string, HotelPhoto[]>;
};

type CampaignPrompt = {
  id: number;
  title: string;
  validStayDateFrom?: string;
  validStayDateTo?: string;
  prompt: {
    id: number;
    title: string;
    description: string;
    linkTitle?: string;
    linkUrl?: string;
    signupButton?: boolean;
  };
};

export const HOTEL_STATUS = {
  LIVE: 'live',
  DRAFT: 'draft',
  PAUSED: 'paused',
  CHURNED: 'churned',
} as const;

export type HotelStatus = (typeof HOTEL_STATUS)[keyof typeof HOTEL_STATUS];

type FacilityType =
  | 'wifi'
  | 'publicBath'
  | 'spa'
  | 'restaurant'
  | 'meetingRoom'
  | 'laundry'
  | 'parking'
  | 'noSmoking'
  | 'pickUpService'
  | 'iceMachine'
  | 'frontDesk24'
  | 'automaticFrontDesk';

type RoomAmenityType =
  | 'wifi'
  | 'refrigerator'
  | 'electricPot'
  | 'moisturizer'
  | 'iron'
  | 'safebox'
  | 'tv'
  | 'telephone'
  | 'toothbrush'
  | 'shampoo'
  | 'towel'
  | 'hairDryer'
  | 'razor'
  | 'slippers'
  | 'yukata'
  | 'cosmetic'
  | 'showerCap';

export type Facilities = Record<FacilityType, { description: string; value: boolean }> & {
  others: {
    description: string;
  }[];
};

export type RoomAmenities = Record<RoomAmenityType, { description: string; value: boolean }> & {
  others: {
    description: string;
  }[];
};

export type HotelRating = {
  averageRating: number;
  totalNoOfReviews: number;
  subRatings: {
    overall: number;
    service: number;
    location: number;
    room: number;
    facility: number;
    bath: number;
    meal: number;
  };
};

export type ReviewSummary = {
  positiveSummary: string;
};

type HotelFromApi = Partial<{
  id: string;
  name: string;
  message?: string;
  bookingEngineExternalLink: string;
  address: Partial<{
    region: string;
    prefecture: string;
    postalAddress: string;
    googleMapsIframe: string;
    latitude: string;
    longitude: string;
  }>;

  meal: string;
  numOfRooms: string;
  parking: string;
  internet: string;
  smokingDetails: string;
  accessibility: string;
  customInformation: {
    plain: string;
    rich: {
      title: string;
      content: string;
    }[];
  };

  room1Name: string;
  room1PhotoUrl: string;
  room2Name: string;
  room2PhotoUrl: string;
  room3Name: string;
  room3PhotoUrl: string;
  cheapestPrice: string;
  tel: string;
  status: HotelStatus;
  accessSimple: string;
  touristHighlights: string[];

  tagBusiness?: boolean;
  tagRyokan?: boolean;
  tagRoomFor3People?: boolean;
  tagBreakfastPlan?: boolean;
  tagFreeWifi?: boolean;
  tagNearStation?: boolean;
  tagFreeCancellation?: boolean;
  tagSafeStay?: boolean;
  tagParking?: boolean;

  tags?: {
    id: string;
    createdAt: string;
    updatedAt: string;
    createdAtUserId: string | null;
    updatedAtUserId: string | null;
    key: string;
    tagName?:
      | string
      | {
          ja: string;
          en: string;
        };
    tagNameJa?: string;
    tagNameEn?: string;
  }[];

  facilities: Facilities;
  facilityFrontDesk24: string;
  facilityFrontDeskAutomatic: string;

  roomAmenities: RoomAmenities;

  rooms: HotelRoom[];

  checkinCheckout: string;

  payment: string | string[];
  creditcard: string;
  precautions: string;
  noShowPolicy: string;
  allowPayAtHotel: boolean;
  allowPrepay: boolean;

  partnershipLevel: PartnershipLevel;

  photos: HotelPhotos;

  webCampaigns: Campaign[];
  webCampaignPrompts?: CampaignPrompt[];
  tllHotelCode: string;

  tabistPaymentTypes: PaymentMethod[];
  propertyPaymentTypes: PropertyPaymentMethod[];
  allowPayAtHotelForForeignGuests: boolean;
  prePaymentRequired: boolean;
  hidePaypayOption: boolean;
  chatbot: {
    key: string;
  };
  faqs: FAQ[];
  hotelRating?: HotelRating;
  reviewSummary?: ReviewSummary;
  createdAt?: string;
  updatedAt?: string;
}>;

export default HotelFromApi;
